import { DEFAULT_FONT_FAMILY } from '../constants';
import { makeTable } from './tableLayout';
import { ensureEditorFonts, measureLineWidth, recomputeTable } from './drawObjects';
import { mathToText } from './texToText';

/**
 * Tables in an OCR transcription → the editor's table object.
 *
 * The transcriber writes a ruled table as a small HTML table (colspan / rowspan, relative
 * column widths in `cols`, relative row heights in `rows`, `border="0"` for an unruled
 * layout, per-cell bold / align / size); older transcripts and other flows write Markdown
 * pipe rows. Both land here and come out as ONE table object — merged cells, unequal
 * columns, tall rows and per-cell formatting included — so a slip or bill keeps its grid.
 */

const PAD_X = 8;
const PAD_Y = 6;
const MIN_FONT = 9;
const MAX_FONT = 16;
const FILL = 0.86; // how much of its cell the longest short line may take
const SHORT_LINE = 34; // longer text wraps in its cell and does not size the table's type

const clampSpan = (value, max) => Math.max(1, Math.min(max, parseInt(value, 10) || 1));

/** An HTML <table> (as a string) → { rows, cols, cells, merges, styles, colsRel, rowsRel, border } or null. */
export function parseHtmlTable(html) {
    const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
    const table = doc.querySelector('table');
    if (!table) return null;
    const rel = (attr) => String(table.getAttribute(attr) || '').split(',')
        .map((v) => parseFloat(v)).filter((n) => Number.isFinite(n) && n > 0);
    const trs = [...table.querySelectorAll('tr')];
    if (!trs.length) return null;

    const occupied = [];
    const cells = [];
    const merges = [];
    const styles = {};
    let cols = 0;
    trs.forEach((tr, r) => {
        occupied[r] = occupied[r] || [];
        cells[r] = cells[r] || [];
        let c = 0;
        for (const td of tr.children) {
            const tag = td.tagName.toLowerCase();
            if (tag !== 'td' && tag !== 'th') continue;
            while (occupied[r][c]) c += 1;
            const cs = clampSpan(td.getAttribute('colspan'), 12);
            const rs = clampSpan(td.getAttribute('rowspan'), trs.length - r);

            const clone = td.cloneNode(true);
            clone.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
            // The OCR's line tags ([bold], [right]…) belong outside tables; one
            // that strays into a cell is dropped rather than printed.
            const text = (clone.textContent || '')
                .replace(/\[\/?(center|right|bold|italic|underline)\]\s*/gi, '')
                .replace(/[^\n]+/g, (row) => mathToText(row))
                .replace(/[ \t ]+/g, ' ').replace(/ ?\n ?/g, '\n').trim();
            const all = (td.textContent || '').replace(/\s+/g, '');
            const boldChars = [...td.querySelectorAll('b, strong')].map((b) => b.textContent || '').join('').replace(/\s+/g, '').length;
            const bold = tag === 'th' || (boldChars > 0 && boldChars >= all.length * 0.9);
            const align = String(td.getAttribute('align') || '').toLowerCase();

            cells[r][c] = text;
            for (let rr = r; rr < r + rs; rr += 1) {
                occupied[rr] = occupied[rr] || [];
                for (let cc = c; cc < c + cs; cc += 1) occupied[rr][cc] = true;
            }
            if (cs > 1 || rs > 1) merges.push({ r, c, rs, cs });
            styles[`${r},${c}`] = {
                bold,
                align: ['center', 'right', 'justify'].includes(align) ? align : undefined,
                size: parseFloat(td.getAttribute('size')) || 1,
            };
            c += cs;
        }
        cols = Math.max(cols, c);
    });
    if (!cols) return null;
    const grid = trs.map((_, r) => Array.from({ length: cols }, (__, c) => cells[r]?.[c] || ''));
    return {
        rows: trs.length,
        cols,
        cells: grid,
        merges,
        styles,
        colsRel: rel('cols'),
        rowsRel: rel('rows'),
        border: table.getAttribute('border') === '0' ? 0 : 1,
    };
}

/** Consecutive Markdown pipe rows → the same shape (equal columns, header row bold). */
export function parsePipeTable(lines) {
    const rows = lines.map((l) => l.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => mathToText(c.trim())));
    const sepAt = rows.findIndex((cells) => cells.length && cells.every((c) => /^:?-{2,}:?$/.test(c)));
    const body = rows.filter((_, i) => i !== sepAt);
    if (!body.length) return null;
    const cols = Math.max(...body.map((r) => r.length));
    const styles = {};
    if (sepAt === 1) for (let c = 0; c < cols; c += 1) styles[`0,${c}`] = { bold: true, size: 1 };
    return {
        rows: body.length,
        cols,
        cells: body.map((r) => Array.from({ length: cols }, (_, c) => r[c] || '')),
        merges: [],
        styles,
        colsRel: [],
        rowsRel: [],
        border: 1,
    };
}

/**
 * A parsed table → a sized table object at (x, y) over `w` points.
 * The type size is the largest at which each cell's longest short line still fits in ~86% of
 * the width it has (so a narrow slip keeps its proportions); row heights follow the text, and
 * the transcriber's relative `rows` are honoured as minimums, scaled so no row is squeezed.
 */
export async function buildTranscriptTable(parsed, { x, y, w }, { scale = 1 } = {}) {
    await ensureEditorFonts([{ fontFamily: DEFAULT_FONT_FAMILY }, { fontFamily: DEFAULT_FONT_FAMILY, bold: true }]);
    const { rows, cols } = parsed;
    const rel = parsed.colsRel.length === cols ? parsed.colsRel : null;
    const relSum = rel ? rel.reduce((a, b) => a + b, 0) : cols;
    const colFracs = rel ? rel.map((v) => v / relSum) : null;
    const widths = (colFracs || Array.from({ length: cols }, () => 1 / cols)).map((f) => f * w);

    const spanOf = (r, c) => parsed.merges.find((m) => m.r === r && m.c === c);
    let best = MAX_FONT;
    for (let r = 0; r < rows; r += 1) {
        for (let c = 0; c < cols; c += 1) {
            const text = parsed.cells[r][c];
            if (!text) continue;
            const m = spanOf(r, c);
            let cellW = 0;
            for (let i = c; i < c + (m ? m.cs : 1); i += 1) cellW += widths[i] || 0;
            const style = parsed.styles[`${r},${c}`] || {};
            for (const line of text.split('\n')) {
                if (!line || line.length > SHORT_LINE) continue;
                const at10 = measureLineWidth({ fontFamily: DEFAULT_FONT_FAMILY, fontSize: 10, bold: !!style.bold }, line);
                if (!at10) continue;
                const size = (10 * FILL * Math.max(8, cellW - PAD_X * 2)) / at10 / (style.size || 1);
                if (size < best) best = size;
            }
        }
    }
    // `scale` < 1 shrinks the whole table (type, padding, rows) — the page asking it to fit.
    const base = Math.max(MIN_FONT, Math.min(MAX_FONT, best)) * scale;

    const t = makeTable({ x, y, w }, rows, cols, {
        fontSize: base,
        fontFamily: DEFAULT_FONT_FAMILY,
        lineHeight: 1.3,
        color: '#1C1815',
        stroke: '#1C1815',
        strokeWidth: 1,
        padX: Math.max(3, PAD_X * scale),
        padY: Math.max(2, PAD_Y * scale),
    });
    t.cells = parsed.cells.map((r) => r.slice());
    if (colFracs) t.colFracs = colFracs;
    if (parsed.merges.length) t.merges = parsed.merges;
    const cellStyles = {};
    for (const [key, s] of Object.entries(parsed.styles)) {
        const own = {};
        if (s.bold) own.bold = true;
        if (s.align) own.align = s.align;
        if (s.size && Math.abs(s.size - 1) > 0.05) own.fontSize = base * s.size;
        if (Object.keys(own).length) cellStyles[key] = own;
    }
    if (Object.keys(cellStyles).length) t.cellStyles = cellStyles;
    t.valign = 'middle';
    if (!parsed.border) t.borderWidth = 0;

    let sized = recomputeTable(t);
    if (parsed.rowsRel.length === rows) {
        // A typical row sets the unit (not the most demanding one — a single big-type row would
        // otherwise inflate every row and the table with it); rows that need more keep their own height.
        const ratios = sized.rowHeights.map((h, r) => h / parsed.rowsRel[r]).sort((a, b) => a - b);
        const unit = ratios[Math.floor(ratios.length / 2)];
        sized = recomputeTable({ ...sized, rowMin: parsed.rowsRel.map((v) => v * unit) });
    }
    return sized;
}
